<template>
    <div>
        <div v-if="!disabled">
            <div>
                <Btn
                    content="Reset"
                    class="mt-3 bg-red-600"
                    type="button"
                    @click="resetElement"
                />
            </div>
            <div
                class="border bg-white dark:bg-gray-700 rounded-lg w-full p-4 shadow-sm max-w-4xl mt-3"
            >
                <p class="text-gray-700 dark:text-gray-200 mb-4">Internal Links to Categories</p>
                <multi-reference
                    create-text="Create new internal link to category and link it to this page"
                    link-text="Link existing internal links to categories"
                    :highlight="categoryDuplicates"
                    :language="language"
                    :linkable="false"
                    :max-items="4"
                    v-model="form.internal_links_to_categories"
                    :content-type-filters="[
                        'All Category'
                    ]"
                    model-data="Internal Link"
                />
            </div>

            <bonus-list-filter
                v-if="filtersLoaded"
                v-model="form.filters"
                :filter-options="filterOptions"
                :language="language"
                :show-filters="true"
                :disabled="loading"
                :tooltip="loading ? 'List is loading' : ''"
                :document-class="documentClass"
                :document-data-id="documentDataId"
                :document-type-id="documentTypeId"
                class="mt-3 max-w-4xl"
                @filter="fetchBonusList"
                @update="fetchUpdatedBonusList"
            />

            <div class="grid grid-cols-3 gap-4 max-w-4xl my-3 rounded-lg shadow border p-4">
                <input-text
                    type="number"
                    :select-on-focus="true"
                    label="* Total rows (Top List)"
                    v-model.number="form.top_list_count"
                    :min="form.hide_top_list ? 0 : 1"
                />

                <input-text
                    type="number"
                    :select-on-focus="true"
                    label="* Rows displayed (Top List)"
                    v-model.number="form.amount_to_show_top"
                    :min="1"
                />

                <input-text
                    type="number"
                    :select-on-focus="true"
                    label="* Rows displayed (Bottom List)"
                    v-model.number="form.amount_to_show_bottom"
                    :min="1"
                />

                <input-checkbox
                    name="display_numbers"
                    label="Display numbers?"
                    class="mt-3"
                    v-model="form.display_numbers"
                />

                <input-checkbox
                    name="hide_top_list"
                    label="Hide Top List"
                    class="mt-3"
                    v-model="form.hide_top_list"
                />

                <input-checkbox
                    name="hide_bottom_list"
                    label="Hide Bottom List"
                    class="mt-3"
                    v-model="form.hide_bottom_list"
                />
            </div>

            <div
                @click="showTable = !showTable"
                class="flex justify-between cursor-pointer mt-4"
            >
                <h2 class="text-black dark:text-white">
                    Bonus List
                    <small class="text-gray-500 dark:text-gray-300" v-if="loading">(Loading...)</small>
                    <small class="text-gray-500 dark:text-gray-300" v-if="!loading && form.bonusList.length">- {{ form.bonusList.length }} casinos</small>
                </h2>

                <div>
                    <svg
                        v-if="!showTable"
                        class="h-4 w-4 cursor-pointer"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M19 9l-7 7-7-7"
                        />
                    </svg>
                    <svg
                        v-if="showTable"
                        class="h-4 w-4 cursor-pointer"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M5 15l7-7 7 7"
                        />
                    </svg>
                </div>
            </div>
            <div
                v-if="showTable"
                :class="{ 'opacity-10': loading }"
            >
                <div
                    v-if="form.bonusList.length === 0"
                    class="mt-4"
                >
                    No casinos found for the selected filters.
                </div>
                <table
                    v-if="form.bonusList.length"
                    class="table-fixed min-w-full divide-y divide-gray-200 shadow my-4">
                    <thead class="bg-gray-50 dark:bg-gray-800 text-black dark:text-white uppercase">
                        <tr>
                            <th></th>
                            <th class="w-1 px-4 py-2 text-left text-xs font-medium tracking-wider">
                                Rank
                            </th>
                            <th class="w-24 px-4 py-2 text-left text-xs font-medium tracking-wider">
                                Casino
                            </th>
                            <th class="px-4 py-2 text-left text-xs font-medium tracking-wider">
                                Bonuses
                            </th>
                        </tr>
                    </thead>
                    <tbody class="bg-white dark:bg-gray-800 text-black dark:text-white divide-y divide-gray-200">
                        <tr v-if="form.bonusList.length" :class="{ 'opacity-30' : form.hide_top_list }">
                            <td colspan="4" class="bg-yellow-200 text-black px-4 py-2 whitespace-nowrap">Top List{{ form.hide_top_list? ' (HIDDEN)' : '' }}</td>
                        </tr>
                        <draggable
                            :value="form.bonusListWithIndicators"
                            @input="(data) => handleUpdateBonusList(data)"
                            class="w-full contents"
                            draggable=".item"
                            @choose="froalaStore.disableFroala = true"
                            @unchoose="froalaStore.disableFroala = false"
                        >
                            <tr
                                v-for="(data, index) in form.bonusListWithIndicators"
                                :key="`bonus-list-${uuid}-row-${index}`"
                                :class="{
                                    item: isNotIndicatorRow(index),
                                    'opacity-30' : hideRow(index),
                                }"
                            >
                                <template v-if="typeof data === 'string'">
                                    <td v-if="data === 'BOTTOM_LIST_INDICATOR'" colspan="4" class="bg-yellow-200 text-black px-4 py-2 whitespace-nowrap">Bottom List{{ hideRow(index) ? ' (HIDDEN)' : '' }}</td>

                                    <td v-if="data === 'TOP_LIST_LOAD_MORE_INDICATOR'" colspan="4" class="bg-yellow-100 text-black px-4 py-1 text-center whitespace-nowrap text-sm">Load More</td>

                                    <td v-if="data === 'BOTTOM_LIST_LOAD_MORE_INDICATOR'" colspan="4" class="bg-yellow-100 text-black px-4 py-1 text-center whitespace-nowrap text-sm">Load More</td>
                                </template>
                                <template v-if="typeof data === 'object'">
                                    <td class="px-1 py-2 whitespace-nowrap cursor-pointer" @click="deleteRow(getRank(index))">
                                        &times;
                                    </td>
                                    <td class="px-4 py-2 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="text-sm font-medium text-gray-900">
                                                <input
                                                    type="number"
                                                    @focus="$event.target.select()"
                                                    :min="1"
                                                    :max="form.bonusList.length + 1"
                                                    :value="getRank(index)"
                                                    @change="handleOrderChange(getRank(index), $event.target.value)"
                                                    class="w-18 bg-white text-black dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-4 py-2 whitespace-nowrap">
                                        <div class="flex items-center">
                                            <div class="text-sm font-medium text-gray-900 dark:text-gray-200 flex items-center gap-1.5">
                                                {{ data.casino.name }}

                                                <a :href="data.casino.edit_route"
                                                   target="_blank"
                                                   v-tooltip="{ content: 'Edit Casino' || '', container: false, placement: 'top', html: false, }"
                                                   class="flex rounded-lg bg-indigo-400 hover:bg-indigo-200 text-white px-1 py-1 focus:outline-none h-6 w-6 flex items-center text-white">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" class="h-6 w-6 fill-current">
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M13 21C13 20.4477 13.4477 20 14 20L15 20V19H14C12.3066 19 11 17.5837 11 15.92C11 15.5261 11.1234 15.1677 11.2468 14.8978C11.3749 14.6178 11.5365 14.3614 11.683 14.1538C11.8315 13.9435 11.9778 13.7646 12.0863 13.6387C12.141 13.5752 12.1874 13.5238 12.2211 13.4871C12.2381 13.4687 12.2519 13.4539 12.2622 13.443L12.2749 13.4297L16 9.55762L19.7184 13.4227L19.7193 13.4236L19.7206 13.425L19.7251 13.4297L19.7378 13.443C19.7481 13.4539 19.7619 13.4687 19.7789 13.4871C19.8126 13.5238 19.859 13.5752 19.9137 13.6387C20.0222 13.7646 20.1685 13.9435 20.317 14.1538C20.4635 14.3614 20.6251 14.6178 20.7532 14.8978C20.8766 15.1677 21 15.5261 21 15.92C21 17.5837 19.6934 19 18 19H17L17 20L18 20C18.5523 20 19 20.4477 19 21C19 21.5523 18.5523 22 18 22H14C13.4477 22 13 21.5523 13 21ZM18 17H14C13.4843 17 13.0003 16.553 13.0003 15.9193C13.002 15.9088 13.0115 15.8481 13.0657 15.7296C13.1251 15.5996 13.2135 15.4538 13.317 15.3071C13.4185 15.1633 13.5222 15.0361 13.6012 14.9445C13.6402 14.8992 13.672 14.864 13.6929 14.8413L13.7153 14.8172L13.7194 14.8129L16 12.4424L18.2793 14.8116L18.2817 14.814L18.2847 14.8172L18.3071 14.8413C18.328 14.864 18.3598 14.8992 18.3988 14.9445C18.4778 15.0361 18.5815 15.1633 18.683 15.3071C18.7865 15.4538 18.8749 15.5996 18.9343 15.7296C18.9885 15.8481 18.9984 15.9095 19 15.92C19 16.5538 18.5157 17 18 17Z"/>
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M2 16C2 8.26801 8.26801 2 16 2C23.732 2 30 8.26801 30 16C30 21.1832 27.1827 25.7073 23.0007 28.1264C20.9405 29.3182 18.5483 30 16 30C8.26801 30 2 23.732 2 16ZM4.18842 13.8702C4.06462 14.5614 4 15.2732 4 16C4 16.4481 4.02457 16.8905 4.07242 17.3259L7.0576 17.2766C7.5844 17.1537 8.11587 17.4731 8.25236 17.9996C8.6107 19.382 9.33476 20.6488 10.3433 21.659C10.6306 21.9468 10.7061 22.3659 10.5699 22.7227C10.5342 22.9438 10.4243 23.1544 10.2429 23.3138L8.19404 25.1144C9.2888 26.0529 10.5546 26.7975 11.9383 27.2952L12.7014 24.4473L12.7047 24.4344C12.8477 23.9009 13.396 23.5844 13.9295 23.7273C15.3386 24.1049 16.7577 24.0818 18.0693 23.7292C18.6027 23.5858 19.1513 23.9019 19.2947 24.4353C19.3005 24.457 19.3056 24.4787 19.3099 24.5005C19.3302 24.5438 19.3477 24.589 19.362 24.6361L20.1586 27.2601C20.7998 27.0233 21.4151 26.7331 21.9993 26.3952C22.6982 25.9909 23.3524 25.5184 23.9528 24.9866L21.8993 23.1656C21.8915 23.1587 21.8838 23.1517 21.8763 23.1446C21.8217 23.1089 21.7697 23.0672 21.7214 23.0197C21.3277 22.6324 21.3225 21.9992 21.7098 21.6055C22.646 20.654 23.3565 19.4547 23.7274 18.0704C23.8704 17.537 24.4187 17.2204 24.9522 17.3633L24.975 17.3697L27.8116 18.1298C27.9354 17.4385 28 16.7268 28 16C28 15.5517 27.9754 15.1092 27.9275 14.6737L24.9086 14.7236C24.7774 14.7258 24.6517 14.7026 24.5361 14.6585C24.1595 14.5977 23.8348 14.3229 23.7293 13.9306C23.3649 12.5752 22.6483 11.334 21.6567 10.3408C21.2665 9.94992 21.2671 9.31675 21.6579 8.92656C21.689 8.89548 21.7217 8.86689 21.7557 8.84077C21.7792 8.81166 21.8046 8.78356 21.8319 8.75665L23.7644 6.85012C22.6792 5.92836 21.4279 5.19621 20.0618 4.70486L19.2953 7.56553C19.1523 8.099 18.604 8.41558 18.0705 8.27264L18.0544 8.26816C16.6506 7.8951 15.2373 7.91932 13.9307 8.27059C13.3974 8.41398 12.8488 8.09786 12.7054 7.56451C12.7013 7.54937 12.6976 7.53422 12.6943 7.51906C12.6693 7.46893 12.6482 7.416 12.6314 7.36055L11.8404 4.74052C10.5511 5.217 9.36602 5.90861 8.32879 6.77179L10.2011 8.77995C10.2267 8.80739 10.2504 8.83589 10.2723 8.86529C10.2963 8.88522 10.3196 8.90645 10.3421 8.92897C10.7323 9.31982 10.7318 9.95298 10.3409 10.3432C9.3797 11.3028 8.65018 12.5202 8.27262 13.9293C8.12967 14.4628 7.58134 14.7794 7.04787 14.6364L7.04014 14.6343L4.18842 13.8702ZM8.27472 9.64665L6.90229 8.17462C5.95599 9.27377 5.20549 10.5463 4.70491 11.9381L6.64735 12.4585C7.04065 11.4164 7.59538 10.4718 8.27472 9.64665ZM13.7685 4.2071L14.3505 6.13466C15.4079 5.95726 16.5068 5.94854 17.6095 6.12957L18.1296 4.1884C17.4385 4.06461 16.7268 4 16 4C15.2375 4 14.4915 4.07112 13.7685 4.2071ZM27.2952 20.062C26.8375 21.3347 26.1707 22.5079 25.336 23.5401L23.8389 22.2125C24.4666 21.4213 24.9815 20.5247 25.3526 19.5415L27.2952 20.062ZM25.1762 8.26674L23.7476 9.67621C24.4853 10.5815 25.0609 11.6107 25.4458 12.7144L27.5348 12.6799C27.0628 11.0371 26.2494 9.53882 25.1762 8.26674ZM6.78647 23.6888L8.28911 22.3683C7.53371 21.4522 6.94542 20.4072 6.55415 19.2852L4.4651 19.3197C4.93158 20.9435 5.73169 22.4262 6.78647 23.6888ZM16 28C15.2732 28 14.5616 27.9354 13.8704 27.8116L14.3906 25.8702C15.4917 26.051 16.5892 26.0425 17.6452 25.8659L18.2303 27.7933C17.5079 27.929 16.7624 28 16 28Z"/>
                                                    </svg>
                                                </a>

                                                <a :href="data.casino_review_edit_route"
                                                   target="_blank"
                                                   v-tooltip="{ content: 'Edit Casino Review' || '', container: false, placement: 'top', html: false, }"
                                                   class="flex rounded-lg bg-indigo-400 hover:bg-indigo-200 text-white px-1 py-1 focus:outline-none h-6 w-6 flex items-center text-white">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" class="h-6 w-6 fill-current">
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.37906 8.01173C9.83757 7.08268 11.1624 7.08267 11.6209 8.01173L12.1927 9.17027L13.4712 9.35605C14.4965 9.50503 14.9058 10.765 14.1639 11.4882L13.2388 12.39L13.4572 13.6633C13.6323 14.6844 12.5606 15.4631 11.6435 14.981L10.5 14.3798L9.35644 14.981C8.43941 15.4631 7.36761 14.6844 7.54275 13.6633L7.76115 12.39L6.836 11.4882C6.09411 10.765 6.50349 9.50503 7.52877 9.35605L8.80729 9.17027L9.37906 8.01173ZM10.5 10.2596L10.4263 10.409C10.2442 10.7779 9.89222 11.0336 9.48508 11.0928L9.32024 11.1167L9.43952 11.233C9.73413 11.5202 9.86856 11.9339 9.79902 12.3394L9.77086 12.5036L9.9183 12.4261C10.2825 12.2346 10.7175 12.2346 11.0817 12.4261L11.2291 12.5036L11.2009 12.3394C11.1314 11.9339 11.2658 11.5202 11.5604 11.233L11.6797 11.1167L11.5149 11.0928C11.1077 11.0336 10.7558 10.7779 10.5737 10.409L10.5 10.2596Z"/>
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M4.37906 17.0117C4.83757 16.0827 6.16238 16.0827 6.6209 17.0117L7.19267 18.1703L8.47118 18.356C9.49645 18.505 9.90585 19.765 9.16395 20.4882L8.23881 21.39L8.4572 22.6633C8.63234 23.6844 7.56055 24.4631 6.64352 23.981L5.49998 23.3798L4.35644 23.981C3.43941 24.4631 2.36761 23.6844 2.54275 22.6633L2.76115 21.39L1.836 20.4882C1.09411 19.765 1.50349 18.505 2.52877 18.356L3.80729 18.1703L4.37906 17.0117ZM5.49998 19.2596L5.42626 19.409C5.24418 19.7779 4.89222 20.0336 4.48508 20.0928L4.32024 20.1167L4.43952 20.233C4.73413 20.5202 4.86857 20.9339 4.79902 21.3394L4.77086 21.5036L4.9183 21.4261C5.28246 21.2346 5.7175 21.2346 6.08165 21.4261L6.22909 21.5036L6.20094 21.3394C6.13139 20.9339 6.26582 20.5202 6.56043 20.233L6.67971 20.1167L6.51487 20.0928C6.10773 20.0336 5.75577 19.7779 5.5737 19.409L5.49998 19.2596Z"/>
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M22.6209 8.01173C22.1624 7.08267 20.8376 7.08268 20.3791 8.01173L19.8073 9.17027L18.5288 9.35605C17.5035 9.50503 17.0941 10.765 17.836 11.4882L18.7611 12.39L18.5428 13.6633C18.3676 14.6844 19.4394 15.4631 20.3564 14.981L21.5 14.3798L22.6435 14.981C23.5606 15.4631 24.6323 14.6844 24.4572 13.6633L24.2388 12.39L25.1639 11.4882C25.9058 10.765 25.4965 9.50503 24.4712 9.35605L23.1927 9.17027L22.6209 8.01173ZM21.4263 10.409L21.5 10.2596L21.5737 10.409C21.7558 10.7779 22.1077 11.0336 22.5149 11.0928L22.6797 11.1167L22.5604 11.233C22.2658 11.5202 22.1314 11.9339 22.2009 12.3394L22.2291 12.5036L22.0817 12.4261C21.7175 12.2346 21.2825 12.2346 20.9183 12.4261L20.7709 12.5036L20.799 12.3394C20.8686 11.9339 20.7341 11.5202 20.4395 11.233L20.3202 11.1167L20.4851 11.0928C20.8922 11.0336 21.2442 10.7779 21.4263 10.409Z"/>
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M25.3791 17.0117C25.8376 16.0827 27.1624 16.0827 27.6209 17.0117L28.1927 18.1703L29.4712 18.356C30.4965 18.505 30.9058 19.765 30.1639 20.4882L29.2388 21.39L29.4572 22.6633C29.6323 23.6844 28.5606 24.4631 27.6435 23.981L26.5 23.3798L25.3564 23.981C24.4394 24.4631 23.3676 23.6844 23.5428 22.6633L23.7611 21.39L22.836 20.4882C22.0941 19.765 22.5035 18.505 23.5288 18.356L24.8073 18.1703L25.3791 17.0117ZM26.5 19.2596L26.4263 19.409C26.2442 19.7779 25.8922 20.0336 25.4851 20.0928L25.3202 20.1167L25.4395 20.233C25.7341 20.5202 25.8686 20.9339 25.799 21.3394L25.7709 21.5036L25.9183 21.4261C26.2825 21.2346 26.7175 21.2346 27.0817 21.4261L27.2291 21.5036L27.2009 21.3394C27.1314 20.9339 27.2658 20.5202 27.5604 20.233L27.6797 20.1167L27.5149 20.0928C27.1077 20.0336 26.7558 19.7779 26.5737 19.409L26.5 19.2596Z"/>
                                                        <path fill-rule="evenodd" clip-rule="evenodd" d="M17.1209 17.0117C16.6624 16.0827 15.3376 16.0827 14.8791 17.0117L14.3073 18.1703L13.0288 18.356C12.0035 18.505 11.5941 19.765 12.336 20.4882L13.2611 21.39L13.0428 22.6633C12.8676 23.6844 13.9394 24.4631 14.8564 23.981L16 23.3798L17.1435 23.981C18.0606 24.4631 19.1323 23.6844 18.9572 22.6633L18.7388 21.39L19.6639 20.4882C20.4058 19.765 19.9965 18.505 18.9712 18.356L17.6927 18.1703L17.1209 17.0117ZM15.9263 19.409L16 19.2596L16.0737 19.409C16.2558 19.7779 16.6077 20.0336 17.0149 20.0928L17.1797 20.1167L17.0604 20.233C16.7658 20.5202 16.6314 20.9339 16.7009 21.3394L16.7291 21.5036L16.5817 21.4261C16.2175 21.2346 15.7825 21.2346 15.4183 21.4261L15.2709 21.5036L15.299 21.3394C15.3686 20.9339 15.2341 20.5202 14.9395 20.233L14.8202 20.1167L14.9851 20.0928C15.3922 20.0336 15.7442 19.7779 15.9263 19.409Z"/>
                                                    </svg>
                                                </a>

                                            </div>
                                        </div>
                                    </td>
                                    <td class="px-4 py-2">
                                        <div class="flex items-center">
                                            <div class="text-sm font-medium text-gray-900 leading-8">
                                                <select2
                                                    :options="data.bonus_options"
                                                    :value="data.bonuses"
                                                    @sync="handleBonusSelectionChange(getRank(index), $event)"
                                                    :key="`bonuses-${data.casino.id}`"
                                                    :multiple="true"
                                                    :id="`bonuses-${data.casino.id}`"
                                                    select-label="name"
                                                    track-by-option="id"
                                                    :name="`bonuses-${data.casino.id}`"
                                                    class="mt-1"
                                                />
                                            </div>
                                        </div>
                                    </td>
                                </template>
                            </tr>
                        </draggable>
                    </tbody>
                </table>
            </div>
        </div>
        <div v-if="disabled" class="text-black dark:text-white">
            ELEMENT DISABLED
        </div>
    </div>
</template>

<script>
import {v4 as uuidv4} from 'uuid';
import draggable from "vuedraggable";
import siteLanguage from "../../config/siteLanguage";
import {useFroalaStore} from "../../stores/froala";

const NORMAL_CATEGORY = 1;
const SOFTWARE_CATEGORY = 2;
const PAYMENT_CATEGORY = 3;
const LICENSE_CATEGORY = 4;
const COUNTRY_CATEGORY = 5;
const BONUS_TYPE_CATEGORY = 6;
const GAME_TYPE_CATEGORY = 7;

export default {
    name: "BonusList",
    components: { draggable },
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },
        language: {
            type: Object,
            required: true,
        },
        value: Object,
        documentClass: {
            type: String,
            required: true,
        },
        documentDataId: {
            type: Number,
            required: false,
        },
        documentTypeId: {
            type: Number,
            required: false,
        },
    },
    data() {
        return {
            errors: this.errorBag || {},
            uuid: null,
            showTable: false,
            loading: false,

            form: {
                hide_top_list: false,
                hide_bottom_list: false,
                bonusList: [],
                bonusListWithIndicators: [],
                indicatorIndices: [],
                filters: {},
                internal_links_to_categories: [],
                display_numbers: true,
                top_list_count: 20,
                amount_to_show_top: 10,
                amount_to_show_bottom: 10,
            },

            categoryDuplicates: [],

            filterOptions: {
                bonusTypes: [],
                gameTypes: [],
                paymentMethods: [],
                payoutRatios: [],
                software: [],
                countries: [],
                currencies: [],
                licenses: [],
            },

            filtersLoaded: false,
            froalaStore: useFroalaStore(),
        };
    },
    mounted: async function () {
        this.uuid = uuidv4();

        await this.fetchFilterOptions();

        if (this.value) {
            if (this.value.filters) {
                this.form.filters = this.value.filters;
            }
            else {
                this.form.filters = {};
                this.setDefaultFilters();
            }
            if (this.value.bonusList) {
                this.setListData(this.value.bonusList);
            }
            this.form.hide_top_list = this.value.hide_top_list || false;
            this.form.hide_bottom_list = this.value.hide_bottom_list || false;
            this.form.bonusList = this.value.bonusList || [];
            this.form.internal_links_to_categories = this.value.internal_links_to_categories || [];
            this.form.display_numbers = this.value.display_numbers ?? true;
            this.form.top_list_count = this.value.top_list_count ?? 20;
            this.form.amount_to_show_top = this.value.amount_to_show_top || 10;
            this.form.amount_to_show_bottom = this.value.amount_to_show_bottom || 10;
        }

        this.filtersLoaded = true;
    },
    methods: {
        async fetchFilterOptions() {
            await axios
                .get(route("api.bonusLists.get-filters"), {
                    params: {
                        languageId: this.language.id ?? siteLanguage.language.ENGLISH,
                    }
                })
                .then((resp) => {
                    this.filterOptions.bonusTypes = resp.data.data.bonusTypes;
                    this.filterOptions.gameTypes = resp.data.data.gameTypes;
                    this.filterOptions.paymentMethods = resp.data.data.paymentMethods;
                    this.filterOptions.payoutRatios = resp.data.data.payoutRatios;
                    this.filterOptions.software = resp.data.data.software;
                    this.filterOptions.countries = resp.data.data.countries;
                    this.filterOptions.currencies = resp.data.data.currencies;
                    this.filterOptions.licenses = resp.data.data.licenses;
                })
                .catch((error) => {
                    console.log("err", error);
                });
        },
        setDefaultFilters (){
            if (! this.documentDataId) {
                return;
            }

            if (this.documentClass === 'Category') {
                let prop = '';
                switch (this.documentTypeId) {
                    case SOFTWARE_CATEGORY:
                        prop = 'software';
                        break;
                    case PAYMENT_CATEGORY:
                        prop = 'paymentMethods';
                        break;
                    case LICENSE_CATEGORY:
                        prop = 'licenses';
                        break;
                    case COUNTRY_CATEGORY:
                        prop = 'countries';
                        break;
                    case BONUS_TYPE_CATEGORY:
                        prop = 'bonusTypes';
                        break;
                    case GAME_TYPE_CATEGORY:
                        prop = 'gameTypes';
                        break;
                }

                if (prop) {
                    let filters = {...this.form.filters};

                    filters[prop] = [this.documentDataId];

                    this.form.filters = {...filters};
                }
            }
        },
        resetElement() {
            this.$confirm({
                message: `Reset element?`,
                button: {
                    no: "No",
                    yes: "Yes",
                },
                callback: (confirm) => {
                    if (confirm) {
                        this.showTable = false;
                        this.loading = false;
                        this.form.hide_top_list = false;
                        this.form.hide_bottom_list = false;
                        this.form.bonusList = [];
                        this.form.bonusListWithIndicators = [];
                        this.form.indicatorIndices = [];
                        this.form.filters = {};
                        this.form.internal_links_to_categories = [];
                        this.form.display_numbers = true;
                        this.form.top_list_count = 20;
                        this.form.amount_to_show_top = 10;
                        this.form.amount_to_show_bottom = 10;
                        this.categoryDuplicates = [];

                        this.setDefaultFilters();
                    }
                },
            });
        },
        isNotIndicatorRow(index) {
            return this.form.indicatorIndices.indexOf(index) < 0;
        },
        getRank(index) {
            return index + 1 - (this.form.indicatorIndices.filter(i => i < index).length);
        },
        handleBonusSelectionChange(rank, bonusIds) {
            let bonusList = [...this.form.bonusList];

            let bonuses = bonusList[rank - 1].bonus_options.filter(bonus => bonusIds.indexOf(bonus.id) >= 0);

            bonusList[rank - 1].bonuses = bonuses;

            this.setListData(bonusList);
        },
        handleOrderChange(prev, next) {
            if (next > this.form.bonusList.length) {
                this.$notify({
                    group: "formError",
                    title: "Invalid Rank",
                    text: `Please enter a number between 1 and ${this.form.bonusList.length}`,
                });

                return;
            }

            let bonusList = [...this.form.bonusList];

            let row = bonusList.splice(prev - 1, 1)[0];

            bonusList.splice(next - 1, 0, row);

            this.setListData(bonusList);
        },
        deleteRow(rank) {
            let bonusList = [...this.form.bonusList];

            bonusList.splice(rank - 1, 1);

            this.setListData(bonusList);
        },
        hideRow(index) {
            let bottomListIndicatorIndex = this.form.bonusListWithIndicators.indexOf('BOTTOM_LIST_INDICATOR');

            let isTopList = bottomListIndicatorIndex < 0 || bottomListIndicatorIndex > index;

            return (isTopList && this.form.hide_top_list) || (!isTopList && this.form.hide_bottom_list);
        },
        handleUpdateBonusList(data) {
            data = data.filter(n => n);
            this.setListData(data);
        },
        async fetchBonusList() {
            this.loading = true;
            await axios
                .get(route("api.bonusLists.get-list", {
                    filters: this.form.filters,
                    language: this.language,
                }))
                .then((resp) => {
                    this.setListData(resp.data.data);
                    this.loading = false;
                })
                .catch((error) => {
                    console.log("err", error);
                    this.loading = false;
                });
        },
        async fetchUpdatedBonusList() {
            this.loading = true;
            await axios
                .post(route("api.bonusLists.get-updated-list"), {
                    filters: this.form.filters,
                    language: this.language,
                    current: this.form.bonusList,
                })
                .then((resp) => {
                    this.setListData(resp.data.data);
                    this.loading = false;
                })
                .catch((error) => {
                    console.log("err", error);
                    this.loading = false;
                });
        },
        setListData(results){
            this.showTable = true;

            results = results.filter(e => typeof e === 'object');

            this.form.indicatorIndices = [];

            const listIncludingIndicators = [];

            let offset = 0;
            for (let i = 0; i < results.length; i++) {
                if (this.form.amount_to_show_top > 0 && i === this.form.amount_to_show_top && i < this.form.top_list_count) {
                    listIncludingIndicators.push('TOP_LIST_LOAD_MORE_INDICATOR');
                    this.form.indicatorIndices.push(i);
                    offset += 1;
                }

                if (
                    (this.form.top_list_count > 0 && i === this.form.top_list_count)
                    || (this.form.hide_top_list && this.form.top_list_count === 0 && i === 0)
                ) {
                    listIncludingIndicators.push('BOTTOM_LIST_INDICATOR');
                    this.form.indicatorIndices.push(i + offset);
                    offset += 1;
                }

                if (this.form.amount_to_show_bottom > 0 && i === this.form.top_list_count + this.form.amount_to_show_bottom && i < results.length) {
                    listIncludingIndicators.push('BOTTOM_LIST_LOAD_MORE_INDICATOR');
                    this.form.indicatorIndices.push(i + offset);
                }

                listIncludingIndicators.push(results[i]);
            }

            this.form.bonusList = results;
            this.form.bonusListWithIndicators = listIncludingIndicators;
        },
    },
    watch: {
        "form.bonusList": function (newValue) {
            this.$emit('input', this.form);
        },
        "form.top_list_count": function (newValue) {
            if (this.form.bonusList.length) {
                this.setListData(this.form.bonusList);
            }
        },
        "form.amount_to_show_top": function (newValue) {
            if (this.form.bonusList.length) {
                this.setListData(this.form.bonusList);
            }
        },
        "form.amount_to_show_bottom": function (newValue) {
            if (this.form.bonusList.length) {
                this.setListData(this.form.bonusList);
            }
        },
        "form.internal_links_to_categories": function (newValue) {
            const existingValues = [];
            this.categoryDuplicates = newValue
                .map((internalLink) => {
                    if (existingValues.includes(internalLink.document.id)) {
                        return {
                            id: internalLink.id,
                            title:
                                "There is already an internal link which connects to this document",
                        };
                    } else {
                        existingValues.push(internalLink.document.id);
                    }
                })
                .filter((item) => !!item);
        },
        "documentDataId": function (newValue) {
            this.setDefaultFilters();
        },
    }
};
</script>
<style scoped>
tr.item {
    cursor: grab;
}

tr.item:active {
    cursor: grabbing;
}
</style>
